<script setup>
import { computed, useSlots } from 'vue';
import { radioRecipe } from './radio.recipe';
import { useRadioGroupContext } from './useRadioGroup';

/**
 * @typedef {object} CsRadioProps
 * @property {string | number} value - The value associated with this radio option
 * @property {string} [id] - Optional ID for the radio input
 * @property {string} [name] - Optional name for the radio input
 * @property {boolean} [isDisabled=false] - Whether the radio is disabled
 */

/**
 * @type {CsRadioProps}
 */
const props = defineProps({
  value: {
    type: [String, Number],
    required: true,
  },
  id: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: 'radio',
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
});

const { rootProps, setValue } = useRadioGroupContext();

const isSelected = computed(() => rootProps.value.value === props.value);

function handleClick(e) {
  e.stopPropagation();
  e.preventDefault();

  if (!props.isDisabled) {
    setValue(props.value);
  }
}

const slots = useSlots();
function hasSlot(name) {
  return slots[name] !== undefined;
}

const radioClasses = computed(() => {
  return radioRecipe({
    isSelected: isSelected.value,
    isDisabled: props.isDisabled,
  });
});
</script>

<template>
  <div :class="radioClasses.container()" @click="handleClick">
    <div :class="radioClasses.inputWrapper()">
      <input
        :id="props.id"
        :name="props.name"
        :value="props.value"
        :checked="isSelected"
        :disabled="props.isDisabled"
        type="radio"
        :class="radioClasses.input()"
        @input="handleClick"
      />

      <label :for="props.id" :class="radioClasses.label()" @click="handleClick">
        <div :class="radioClasses.dot()" />
      </label>
    </div>

    <label
      v-if="hasSlot('default')"
      :for="props.id"
      :class="radioClasses.textLabel()"
    >
      <slot />
    </label>
  </div>
</template>
